// File Metadata:
// Filename: todoRtkApi.js
// Version: 1.00
// Created: 2025-06-29 00:00:00
// Last Modified: 2025-06-29 00:00:00
// Authors: Dieuwer Descheemaecker, AI Assistant
// Description: RTK Query API slice for todo operations

import { baseApi } from '../../../store/api/baseApi';

export const todoRtkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get todos with filtering and sorting
    getTodos: builder.query({
      query: (params = {}) => ({
        url: '/todos',
        params,
      }),
      providesTags: ['Todo'],
      keepUnusedDataFor: 300, // 5 minutes cache
    }),

    // Get a specific todo by ID
    getTodoById: builder.query({
      query: (id) => `/todos/${id}`,
      providesTags: (result, error, id) => [{ type: 'Todo', id }],
    }),

    // Create a new todo
    createTodo: builder.mutation({
      query: (todoData) => ({
        url: '/todos',
        method: 'POST',
        body: todoData,
      }),
      invalidatesTags: ['Todo'],
      // Optimistic update
      async onQueryStarted(todoData, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodos', undefined, (draft) => {
            // Add optimistic todo with temporary ID
            const optimisticTodo = {
              _id: `temp-${Date.now()}`,
              ...todoData,
              status: 'pending',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            draft.todos?.unshift(optimisticTodo);
          })
        );
        
        try {
          const result = await queryFulfilled;
          
          // Update with real todo data
          dispatch(
            todoRtkApi.util.updateQueryData('getTodos', undefined, (draft) => {
              const index = draft.todos?.findIndex(todo => todo._id === `temp-${Date.now()}`);
              if (index !== -1 && draft.todos) {
                draft.todos[index] = result.data;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Update a todo
    updateTodo: builder.mutation({
      query: ({ id, ...todoData }) => ({
        url: `/todos/${id}`,
        method: 'PUT',
        body: todoData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Todo', id },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodos', undefined, (draft) => {
            const todo = draft.todos?.find(todo => todo._id === id);
            if (todo) {
              Object.assign(todo, { ...patch, updatedAt: new Date().toISOString() });
            }
          })
        );
        
        // Also update individual todo cache
        const individualPatchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', id, (draft) => {
            Object.assign(draft, { ...patch, updatedAt: new Date().toISOString() });
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          individualPatchResult.undo();
        }
      },
    }),

    // Update todo status
    updateTodoStatus: builder.mutation({
      query: ({ id, ...statusData }) => ({
        url: `/todos/${id}/status`,
        method: 'PUT',
        body: statusData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Todo', id },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ id, ...statusData }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodos', undefined, (draft) => {
            const todo = draft.todos?.find(todo => todo._id === id);
            if (todo) {
              Object.assign(todo, { ...statusData, updatedAt: new Date().toISOString() });
            }
          })
        );
        
        const individualPatchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', id, (draft) => {
            Object.assign(draft, { ...statusData, updatedAt: new Date().toISOString() });
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          individualPatchResult.undo();
        }
      },
    }),

    // Delete a todo
    deleteTodo: builder.mutation({
      query: (id) => ({
        url: `/todos/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Todo'],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodos', undefined, (draft) => {
            if (draft.todos) {
              draft.todos = draft.todos.filter(todo => todo._id !== id);
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Add a subtask to a todo
    addSubtask: builder.mutation({
      query: ({ todoId, ...subtaskData }) => ({
        url: `/todos/${todoId}/subtasks`,
        method: 'POST',
        body: subtaskData,
      }),
      invalidatesTags: (result, error, { todoId }) => [
        { type: 'Todo', id: todoId },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ todoId, ...subtaskData }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
            if (!draft.subtasks) draft.subtasks = [];
            draft.subtasks.push({
              _id: `temp-${Date.now()}`,
              ...subtaskData,
              done: false,
              createdAt: new Date().toISOString(),
            });
          })
        );
        
        try {
          const result = await queryFulfilled;
          // Update with real subtask data
          dispatch(
            todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
              draft.subtasks = result.data.subtasks;
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Update subtask status
    updateSubtaskStatus: builder.mutation({
      query: ({ todoId, subtaskId, ...statusData }) => ({
        url: `/todos/${todoId}/subtasks/${subtaskId}`,
        method: 'PUT',
        body: statusData,
      }),
      invalidatesTags: (result, error, { todoId }) => [
        { type: 'Todo', id: todoId },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ todoId, subtaskId, ...statusData }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
            const subtask = draft.subtasks?.find(st => st._id === subtaskId);
            if (subtask) {
              Object.assign(subtask, statusData);
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Delete a subtask
    deleteSubtask: builder.mutation({
      query: ({ todoId, subtaskId }) => ({
        url: `/todos/${todoId}/subtasks/${subtaskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { todoId }) => [
        { type: 'Todo', id: todoId },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ todoId, subtaskId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
            if (draft.subtasks) {
              draft.subtasks = draft.subtasks.filter(st => st._id !== subtaskId);
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Add a comment to a todo
    addComment: builder.mutation({
      query: ({ todoId, ...commentData }) => ({
        url: `/todos/${todoId}/comments`,
        method: 'POST',
        body: commentData,
      }),
      invalidatesTags: (result, error, { todoId }) => [
        { type: 'Todo', id: todoId },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ todoId, ...commentData }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
            if (!draft.comments) draft.comments = [];
            draft.comments.push({
              _id: `temp-${Date.now()}`,
              ...commentData,
              createdAt: new Date().toISOString(),
            });
          })
        );
        
        try {
          const result = await queryFulfilled;
          // Update with real comment data
          dispatch(
            todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
              draft.comments = result.data.comments;
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Delete a comment
    deleteComment: builder.mutation({
      query: ({ todoId, commentId }) => ({
        url: `/todos/${todoId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { todoId }) => [
        { type: 'Todo', id: todoId },
        'Todo'
      ],
      // Optimistic update
      async onQueryStarted({ todoId, commentId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          todoRtkApi.util.updateQueryData('getTodoById', todoId, (draft) => {
            if (draft.comments) {
              draft.comments = draft.comments.filter(comment => comment._id !== commentId);
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetTodosQuery,
  useGetTodoByIdQuery,
  useCreateTodoMutation,
  useUpdateTodoMutation,
  useUpdateTodoStatusMutation,
  useDeleteTodoMutation,
  useAddSubtaskMutation,
  useUpdateSubtaskStatusMutation,
  useDeleteSubtaskMutation,
  useAddCommentMutation,
  useDeleteCommentMutation,
} = todoRtkApi;

// Export the API slice for potential direct use
export default todoRtkApi;
